import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

// Layout Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Disclaimer from "./pages/Disclaimer";
import Terms from "./pages/TermsConditions";

import Home from "./pages/Home";
import JobDetails from "./pages/JobDetails";

// Lazy Pages
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CodeEditor = lazy(() => import("./pages/CodeEditor"));
const Interview = lazy(() => import("./interview/main-interview"));
const NotFound = lazy(() => import("./pages/Notfound"));

// Interview Pages
const Html = lazy(() => import("./interview/html"));
const Css = lazy(() => import("./interview/css"));
const Bootstrap = lazy(() => import("./interview/bootstrap"));
const Express = lazy(() => import("./interview/express"));
const Nodejs = lazy(() => import("./interview/nodejs"));
const Python = lazy(() => import("./interview/python"));
const ReactInterview = lazy(() => import("./interview/react"));
const Sql = lazy(() => import("./interview/sql"));
const Javascript = lazy(() => import("./interview/javascript"));
const Javascriptcoding = lazy(() => import("./interview/javascriptcoding"));
const Pythoncoding = lazy(() => import("./interview/pythoncoding"));

/* 🔥 Scroll to Top */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/* 🔥 Loader */
const Loader = () => (
  <div style={{ textAlign: "center", padding: "50px" }}>
    <p>Loading...</p>
  </div>
);

const App = () => {
  return (
    <Router>
      <ScrollToTop />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-grow container mx-auto px-4 py-8">
          <Suspense fallback={<Loader />}>
            <Routes>

              {/* HOME */}
              <Route path="/" element={
                <>
                  <Helmet>
                    <title>JobKickstart - Top Interview Questions & Jobs</title>
                    <meta
                      name="description"
                      content="Prepare for interviews with HTML, CSS, JavaScript, React and backend questions."
                    />
                  </Helmet>
                  <Home />
                </>
              } />

              {/* JOB */}
              <Route path="/job/:id" element={<JobDetails />} />

              {/* INTERVIEW HUB */}
              <Route path="/interview" element={<Interview />} />

              {/* HTML */}
              <Route path="/html-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top HTML Interview Questions (2026)</title>
                    <meta name="description" content="Top HTML interview questions with answers for freshers and experienced developers." />
                  </Helmet>
                  <Html />
                </>
              } />

              {/* CSS */}
              <Route path="/css-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top CSS Interview Questions (2026)</title>
                    <meta name="description" content="Most asked CSS interview questions with answers and examples." />
                  </Helmet>
                  <Css />
                </>
              } />

              {/* JAVASCRIPT */}
              <Route path="/javascript-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top JavaScript Interview Questions (2026)</title>
                    <meta name="description" content="JavaScript interview questions for beginners and experienced developers." />
                  </Helmet>
                  <Javascript />
                </>
              } />

              {/* REACT */}
              <Route path="/react-js-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top React Interview Questions (2026)</title>
                    <meta name="description" content="React interview questions with answers, hooks, state, lifecycle explained." />
                  </Helmet>
                  <ReactInterview />
                </>
              } />

              {/* NODE */}
              <Route path="/node-js-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top Node.js Interview Questions (2026)</title>
                    <meta name="description" content="Node.js backend interview questions with real-world examples." />
                  </Helmet>
                  <Nodejs />
                </>
              } />

              {/* PYTHON */}
              <Route path="/python-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top Python Interview Questions (2026)</title>
                    <meta name="description" content="Python interview questions for beginners and experienced developers." />
                  </Helmet>
                  <Python />
                </>
              } />

              {/* SQL */}
              <Route path="/sql-interview-questions" element={
                <>
                  <Helmet>
                    <title>Top SQL Interview Questions (2026)</title>
                    <meta name="description" content="SQL queries and interview questions with examples." />
                  </Helmet>
                  <Sql />
                </>
              } />

              {/* BOOTSTRAP */}
              <Route path="/bootstrap-interview-questions" element={<Bootstrap />} />

              {/* EXPRESS */}
              <Route path="/express-js-interview-questions" element={<Express />} />


              {/* CODING */}
              <Route path="/javascript-coding-interview-questions" element={
                <>
                  <Helmet>
                    <title>JavaScript Coding Interview Questions</title>
                    <meta name="description" content="Practice JavaScript coding problems for interviews." />
                  </Helmet>
                  <Javascriptcoding />
                </>
              } />

              <Route path="/python-coding-interview-questions" element={
                <>
                  <Helmet>
                    <title>Python Coding Interview Questions</title>
                    <meta name="description" content="Solve Python coding interview questions with solutions." />
                  </Helmet>
                  <Pythoncoding />
                </>
              } />

              {/* TOOLS */}
              <Route path="/editor-code" element={<CodeEditor />} />

              {/* INFO PAGES */}
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/disclaimer" element={<Disclaimer />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />

            </Routes>
          </Suspense>
        </main>

        <Footer />
      </div>
    </Router>
  );
};

export default App;